import React, { useEffect } from "react";
import moment from "moment";
import styled from "styled-components";

// ─── Now-indicator overlay: one line + dot across the day columns only ─────────
// Hides FullCalendar's built-in per-column line/arrow (via FcWrap CSS) and draws
// a single line + dot positioned over the day-columns area, never over the time
// axis labels. Pass the FcWrap element ref and deps that change on view/date.
export function useNowIndicatorOverlay(wrapRef, deps = []) {
  useEffect(() => {
    const draw = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      wrap.querySelectorAll(".mp-now-line, .mp-now-dot").forEach((e) => e.remove());

      const now = new Date();
      const dec = now.getHours() + now.getMinutes() / 60;
      if (dec < 8 || dec >= 24) return;

      const pad = (n) => String(n).padStart(2, "0");
      const lane = wrap.querySelector(
        `.fc-timegrid-slots td.fc-timegrid-slot[data-time="${pad(now.getHours())}:00:00"]`
      );
      const cols = wrap.querySelectorAll(".fc-timegrid-cols .fc-timegrid-col[data-date]");
      const host = wrap.querySelector(".fc-timegrid-cols");
      if (!lane || !cols.length || !host) return;

      const hostRect = host.getBoundingClientRect();
      const laneRect = lane.getBoundingClientRect();
      const y = laneRect.top - hostRect.top + (now.getMinutes() / 60) * laneRect.height;
      const x1 = cols[0].getBoundingClientRect().left - hostRect.left;
      const x2 = cols[cols.length - 1].getBoundingClientRect().right - hostRect.left;

      const line = document.createElement("div");
      line.className = "mp-now-line";
      line.style.top = `${y}px`;
      line.style.left = `${x1}px`;
      line.style.width = `${x2 - x1}px`;
      host.appendChild(line);

      const dot = document.createElement("div");
      dot.className = "mp-now-dot";
      dot.style.top = `${y - 5}px`;
      dot.style.left = `${x1 - 5}px`;
      host.appendChild(dot);
    };

    const t = setTimeout(draw, 500);
    const iv = setInterval(draw, 60000);
    window.addEventListener("resize", draw);
    return () => {
      clearTimeout(t);
      clearInterval(iv);
      window.removeEventListener("resize", draw);
      wrapRef.current
        ?.querySelectorAll(".mp-now-line, .mp-now-dot")
        .forEach((e) => e.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// ─── Chevron arrows (Insyts design assets) ─────────────────────────────────────

export const ChevronLeft = ({ size = 9 }) => (
  <svg width={size} height={(size * 16) / 9} viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 15L1 8L8 1" stroke="#00685F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronRight = ({ size = 9 }) => (
  <svg width={size} height={(size * 16) / 9} viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 15L8 8L1 1" stroke="#00685F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Custom navigation bar (date range + chevrons + view toggle) ───────────────

const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 2px solid #d9d9d9;
`;

const DateNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DateRangeText = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #1b1b1d;
  letter-spacing: -0.64px;
`;

const NavBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #00685f;
  padding: 0 2px;
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  display: flex;
  align-items: center;
  &:hover { opacity: 0.65; }
`;

const ViewGroup = styled.div`
  display: flex;
  border: 1px solid #e2e2e8;
  border-radius: 4px;
  overflow: hidden;
`;

const ViewBtn = styled.button`
  padding: 6px 22px;
  border: none;
  border-right: ${({ $notlast }) => ($notlast ? "1px solid #e2e2e8" : "none")};
  background: ${({ $active }) => ($active ? "#636980" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#636980")};
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${({ $active }) => ($active ? "#636980" : "#f5f5f5")}; }
`;

export function CalendarNavBar({
  calendarRef,
  title,
  view,
  onViewChange,
  viewOptions = [
    { key: "timeGridWeek", label: "Week" },
    { key: "dayGridMonth", label: "Month" },
  ],
}) {
  const api = () => calendarRef?.current?.getApi();
  const handlePrev = () => api()?.prev();
  const handleNext = () => api()?.next();
  const handleView = (key) => {
    api()?.changeView(key);
    onViewChange?.(key);
  };

  return (
    <NavBar>
      <DateNavGroup>
        <DateRangeText>{title}</DateRangeText>
        <NavBtn onClick={handlePrev} aria-label="Previous"><ChevronLeft size={9} /></NavBtn>
        <NavBtn onClick={handleNext} aria-label="Next"><ChevronRight size={9} /></NavBtn>
      </DateNavGroup>
      <ViewGroup>
        {viewOptions.map((opt, i) => (
          <ViewBtn
            key={opt.key}
            $active={view === opt.key}
            $notlast={i < viewOptions.length - 1}
            onClick={() => handleView(opt.key)}
          >
            {opt.label}
          </ViewBtn>
        ))}
      </ViewGroup>
    </NavBar>
  );
}

// ─── Mini month calendar (pure CSS, matches My Planner) ────────────────────────

const MiniCalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 40px;
  margin-bottom: 4px;
  background: #f6f6f6;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 400;
  color: #1f243b;
`;

const MiniNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MiniNavBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { opacity: 0.65; }
`;

const MiniGridBox = styled.div`
  background: #f6f6f6;
  border-radius: 8px;
  padding: 10px 12px 14px;
`;

const MiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
`;

const MiniWeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding-bottom: 4px;
  margin-bottom: 6px;
  border-bottom: 2px solid #ffffff;
`;

const MiniWeekday = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #636980;
`;

const MiniDay = styled.button`
  height: 26px;
  width: 26px;
  border: none;
  background: ${({ $today }) => ($today ? "#1f243b" : "transparent")};
  color: ${({ $today, $muted }) =>
    $today ? "#fff" : $muted ? "#d9d9d9" : "#1f243b"};
  font-weight: ${({ $today }) => ($today ? 600 : 400)};
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0 auto;
  transition: background 0.12s;
  &:hover {
    background: ${({ $today }) => ($today ? "#1f243b" : "#ececec")};
  }
`;

export function MiniMonth({ date, onPick }) {
  const base = date ? moment(date) : moment();
  const monthIdx = base.month();
  const gridStart = base.clone().startOf("month").startOf("week");
  const today = moment().format("YYYY-MM-DD");
  const cells = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, "days"));

  return (
    <MiniGridBox>
      <MiniWeekRow>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <MiniWeekday key={d}>{d}</MiniWeekday>
        ))}
      </MiniWeekRow>
      <MiniGrid>
        {cells.map((c) => {
          const key = c.format("YYYY-MM-DD");
          return (
            <MiniDay
              key={key}
              $muted={c.month() !== monthIdx}
              $today={key === today}
              onClick={() => onPick && onPick(c)}
            >
              {c.date()}
            </MiniDay>
          );
        })}
      </MiniGrid>
    </MiniGridBox>
  );
}

export function MiniCalendar({ date, onPrev, onNext, onPick }) {
  const label = date ? moment(date).format("MMMM, YYYY") : moment().format("MMMM, YYYY");
  return (
    <div>
      <MiniCalHead>
        <span>{label}</span>
        <MiniNavGroup>
          <MiniNavBtn onClick={onPrev}><ChevronLeft size={8} /></MiniNavBtn>
          <MiniNavBtn onClick={onNext}><ChevronRight size={8} /></MiniNavBtn>
        </MiniNavGroup>
      </MiniCalHead>
      <MiniMonth date={date} onPick={onPick} />
    </div>
  );
}

// ─── Month navigation header (bold range + teal chevrons) ──────────────────────

const MonthNavWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const MonthNavLabel = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #1b1b1d;
  letter-spacing: -0.64px;
`;

export function MonthNav({ label, onPrev, onNext }) {
  return (
    <MonthNavWrap>
      <NavBtn onClick={onPrev} aria-label="Previous"><ChevronLeft size={9} /></NavBtn>
      <MonthNavLabel>{label}</MonthNavLabel>
      <NavBtn onClick={onNext} aria-label="Next"><ChevronRight size={9} /></NavBtn>
    </MonthNavWrap>
  );
}

// ─── Two-line day header (weekday + date box) ──────────────────────────────────

export function renderDayHeader(arg) {
  const d = moment(arg.date);
  if (arg.view.type === "dayGridMonth") {
    return (
      <span style={{ fontSize: 16, fontWeight: 500, color: "#636980", letterSpacing: "-0.64px" }}>
        {d.format("ddd")}
      </span>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
      <span style={{ fontSize: 16, fontWeight: 500, color: "#636980", letterSpacing: "-0.64px" }}>
        {d.format("ddd")}
      </span>
      <span style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 37, height: 26,
        fontSize: 18, fontWeight: 600, letterSpacing: "-0.64px",
        color: arg.isToday ? "#fff" : "#1f243b",
        background: arg.isToday ? "#1f243b" : "transparent",
        borderRadius: 3,
      }}>
        {d.format("D")}
      </span>
    </div>
  );
}

// ─── Shared FullCalendar props (My Planner visual config) ──────────────────────

export const SHELL_TIMEGRID_PROPS = {
  height: "auto",
  contentHeight: "auto",
  slotMinTime: "00:00:00",
  slotMaxTime: "24:00:00",
  scrollTime: "08:00:00",
  slotDuration: "01:00:00",
  slotLabelFormat: { hour: "numeric", minute: "2-digit", hour12: true },
  nowIndicator: true,
  dayHeaderContent: renderDayHeader,
};

// ─── FullCalendar CSS overrides — exact My Planner look ─────────────────────────

export const FcWrap = styled.div`
  .fc .fc-day-today { background: rgba(0, 104, 95, 0.04) !important; }

  /* ── Now indicator: hide FC's built-in line/arrow; a single overlay line + dot
        is drawn across the day columns only (never over the time axis) ── */
  .fc .fc-timegrid-now-indicator-line { display: none !important; }
  .fc .fc-timegrid-now-indicator-arrow { display: none !important; }
  .mp-now-line {
    position: absolute;
    height: 0;
    border-top: 2px solid #ba1a1a;
    z-index: 6;
    pointer-events: none;
  }
  .mp-now-dot {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ba1a1a;
    z-index: 7;
    pointer-events: none;
  }

  /* ── Container: card stroke + sharp corners ── */
  .fc {
    --fc-border-color: #d9d9d9;
    --fc-now-indicator-color: #ba1a1a;
    --fc-today-bg-color: transparent;
    border: 1px solid #e2e2e8;
    border-radius: 0;
    overflow: hidden;
    font-family: 'DM Sans', sans-serif;
  }
  .fc .fc-scrollgrid { border-radius: 0; border: none; }
  .fc .fc-scrollgrid-section > * { border-right: none; }
  .fc table { border-top: none; }

  /* ── Header row: #FCF8FA, 71px ── */
  .fc .fc-col-header { background: #fcf8fa; }
  .fc .fc-col-header-cell { height: 71px; vertical-align: middle; border-color: #d9d9d9; }
  .fc .fc-col-header-cell-cushion {
    width: 100%;
    padding: 14px 4px;
    text-decoration: none !important;
  }
  .fc .fc-timegrid-axis { background: #fcf8fa; border-color: #d9d9d9; }

  /* ── Time slot rows ── */
  .fc .fc-timegrid-slot { height: 44px; border-color: #d9d9d9; }
  .fc .fc-timegrid-slot-minor { border-top-style: none; }
  .fc .fc-timegrid-slot-label-cushion { font-size: 14px; color: #636980; padding: 0 8px; }
  .fc .fc-timegrid-axis-cushion { font-size: 14px; color: #636980; }

  /* ── Vertical day column separators ── */
  .fc .fc-timegrid-col, .fc .fc-daygrid-day { border-color: #d9d9d9; }

  /* ── Month view day numbers ── */
  .fc .fc-daygrid-day-number { font-size: 14px; color: #1f243b; padding: 6px 8px; }

  /* ── More-link pill ── */
  .fc .fc-more-link { background: transparent !important; box-shadow: none !important; }

  /* ── Default FullCalendar toolbar hidden (we use a custom one) ── */
  .fc .fc-toolbar.fc-header-toolbar { display: none; }
`;
